<template>
  <!--
    称号徽标：全站唯一称号渲染组件。
    五处承载点（玩家列表 / 聊天 / 好友 / 排行榜 / 资料页）都走这里，
    不要各自写一套样式——否则三个月后必然漂移成五种样子。

    关键 props：
      ground   'light' | 'dark'  明区白底 / 暗区深紫底，决定取哪套色值（聊天区必须 dark）
      variant  'inline' | 'two-line' | 'card'
      maxChars 该场景的字数上限，超出走短名 → 语义断点 → 硬截三层
  -->
  <span
    class="title-badge"
    :class="[`tb-${variant}`, `tb-ground-${ground}`, { 'tb-dim': dim }]"
    :style="badgeStyle"
    :aria-label="ariaLabel"
    :tabindex="canExpand ? 0 : -1"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focus="onEnter"
    @blur="onLeave"
    @click.stop="onTap"
  >
    <i v-if="seal" class="tb-seal" :style="{ color: style.sealColor }">{{ seal }}</i>
    <span class="tb-text">{{ info.text }}</span>

    <!-- 完整查看卡：fixed 定位，避免被好友/玩家列表的 overflow 裁掉 -->
    <div v-if="cardVisible" class="tb-pop" :style="cardStyle" @mouseenter="cancelLeave" @mouseleave="onLeave">
      <div class="tb-pop-head">
        <i v-if="seal" class="tb-seal tb-pop-seal">{{ seal }}</i>
        <span class="tb-pop-title">{{ title }}</span>
      </div>
      <div class="tb-pop-row" v-if="typeName">类别　{{ typeName }}</div>
      <div class="tb-pop-row" v-if="evaluateDate">获评　{{ evaluateDate }}</div>
    </div>
  </span>
</template>

<script>
import { sealOf, cutTitle, tierStyle, plainTierStyle } from '../../utils/title'

export default {
  name: 'TitleBadge',
  props: {
    /** 称号全名 */
    title: { type: String, default: '' },
    /** 数据层短名（2-4 字），可为空 */
    shortTitle: { type: String, default: '' },
    /** 视觉档位 1-4 */
    tier: { type: [Number, String], default: 2 },
    /** 称号类别名，如「创作类」 */
    typeName: { type: String, default: '' },
    /** 评定日期 yyyy-MM-dd */
    evaluateDate: { type: String, default: '' },
    /** inline 跟名字同行 / two-line 独占第二行 / plain 正文行内（资料页、情报卡） */
    variant: { type: String, default: 'inline' },
    /** light 白底 / dark 深紫底 */
    ground: { type: String, default: 'light' },
    /** 字数上限 */
    maxChars: { type: [Number, String], default: 8 },
    /** 是否允许展开完整卡 */
    expandable: { type: Boolean, default: true },
    /** 离线/降级显示：降透明度，不要改灰色（灰色会与 T1 混淆） */
    dim: { type: Boolean, default: false }
  },
  data () {
    return {
      cardVisible: false,
      cardStyle: {},
      enterTimer: null,
      leaveTimer: null
    }
  },
  computed: {
    tierNum () {
      const n = parseInt(this.tier, 10)
      return isNaN(n) ? 2 : n
    },
    style () {
      // plain 是「嵌在正文里的称号」，只换字色，不换材质；其余形态按明暗底取档位材质
      if (this.variant === 'plain') return plainTierStyle(this.tierNum)
      return tierStyle(this.tierNum, this.ground)
    },
    seal () {
      // plain 落在正文行里，称号全名本来就是完整显示的，再加一枚「花」只是把同一个词说两遍
      if (this.variant === 'plain') return ''
      return sealOf(this.title)
    },
    info () {
      return cutTitle(this.title, this.shortTitle, parseInt(this.maxChars, 10))
    },
    canExpand () {
      return this.expandable && !!this.title
    },
    ariaLabel () {
      if (!this.title) return ''
      const parts = ['称号：' + this.title]
      if (this.typeName) parts.push(this.typeName)
      return parts.join('，')
    },
    badgeStyle () {
      const s = this.style
      if (this.variant === 'plain') {
        // 无底无框：这一形态要能直接混在「ID　wang」这样的正文行里
        return { color: s.color, fontWeight: 'bold', background: 'transparent', borderColor: 'transparent' }
      }
      return {
        color: s.color,
        fontWeight: s.fontWeight,
        background: s.bg,
        borderColor: s.border
      }
    }
  },
  beforeDestroy () {
    this.clearTimers()
  },
  methods: {
    clearTimers () {
      if (this.enterTimer) { clearTimeout(this.enterTimer); this.enterTimer = null }
      if (this.leaveTimer) { clearTimeout(this.leaveTimer); this.leaveTimer = null }
    },
    onEnter () {
      if (!this.canExpand) return
      this.clearTimers()
      // 120ms 延时：鼠标扫过列表不该到处弹卡
      this.enterTimer = setTimeout(() => { this.showCard() }, 120)
    },
    onLeave () {
      this.clearTimers()
      // 留一点缓冲，鼠标从徽标移进卡片时不会闪断
      this.leaveTimer = setTimeout(() => { this.cardVisible = false }, 160)
    },
    cancelLeave () {
      this.clearTimers()
    },
    onTap () {
      // 移动端没有 hover（站内已用 @media (hover:none) 处理同类问题），点按即展开
      if (!this.canExpand) return
      if (this.cardVisible) { this.cardVisible = false } else { this.showCard() }
    },
    showCard () {
      if (!this.canExpand) return
      this.cardVisible = true
      // 等 DOM 渲染后再量位置
      this.$nextTick(() => { this.computeCardPosition() })
    },
    computeCardPosition () {
      if (!this.$el || !this.$el.getBoundingClientRect) return
      const rect = this.$el.getBoundingClientRect()
      const cardW = 200
      const gap = 6
      let left = rect.left + rect.width / 2 - cardW / 2
      const maxLeft = window.innerWidth - cardW - 8
      if (left < 8) left = 8
      if (left > maxLeft) left = maxLeft
      // 优先放下方；下方不够就翻到上方
      let top = rect.bottom + gap
      if (top + 90 > window.innerHeight) {
        top = rect.top - gap - 90
        if (top < 8) top = 8
      }
      this.cardStyle = { left: left + 'px', top: top + 'px', width: cardW + 'px' }
    }
  }
}
</script>

<style scoped>
.title-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  line-height: 14px;
  padding: 0 3px;
  border: 1px solid transparent;
  border-radius: 2px;
  vertical-align: middle;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  cursor: default;
  /* 全站无动效，只允许这一处 120ms 的透明度反馈 */
  transition: opacity 120ms linear;
  outline: none;
}
.title-badge:focus {
  border-style: dashed;
}
.tb-inline {
  display: inline-flex;
  margin-left: 4px;
}
.tb-two-line {
  display: flex;
  margin-top: 1px;
  max-width: 100%;
}
.tb-plain {
  /* 资料页/情报卡：与同行 14px 正文同字号，只靠字重和字色显身份 */
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 14px;
  line-height: 20px;
  font-weight: bold;
  padding: 0;
  border: none;
  background: transparent;
  vertical-align: baseline;
  cursor: pointer;
}
/* 基类的 :focus 虚线是给列表徽标用的；正文行里的称号聚焦时不该多出一个框 */
.tb-plain:focus {
  border: none;
}
.tb-plain .tb-seal {
  /* 一枚小印：方形描边，比裸字更像「图章」，又不至于抢眼 */
  font-size: 10px;
  line-height: 12px;
  padding: 0 2px;
  border: 1px solid currentColor;
  border-radius: 2px;
  opacity: 0.85;
}
.tb-dim {
  opacity: 0.6;
}
.tb-seal {
  font-style: normal;
  font-weight: normal;
  font-size: 10px;
  line-height: 1;
  opacity: 0.9;
  flex-shrink: 0;
}
.tb-text {
  /* flex:1 + min-width:0 是省略号生效的前提；只写 overflow:hidden 的话，
     flex 子项不肯收缩，窄屏上会直接裁切而不是出「…」 */
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 完整查看卡（类名必须与宿主区分：早前宿主也叫 .tb-card，
   弹卡的 position:fixed + 蓝边框就套到徽标身上，资料页里变成了一个小方框） */
.tb-pop {
  position: fixed;
  z-index: 2100;
  background: #fdfdf5;
  border: 1px solid #0080ff;
  border-radius: 3px;
  padding: 7px 9px;
  box-shadow: 0 2px 8px rgba(45, 35, 64, 0.28);
  font-size: 12px;
  line-height: 18px;
  color: #333;
  font-weight: normal;
  white-space: normal;
  text-align: left;
}
.tb-pop-head {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-bottom: 4px;
}
.tb-pop-title {
  font-size: 13px;
  font-weight: bold;
  color: #0080ff;
  word-break: break-all;
}
.tb-pop-seal {
  font-size: 12px;
  color: #0080ff !important;
}
.tb-pop-row {
  color: #666;
  font-size: 12px;
}
@media (max-width: 768px) {
  .title-badge:not(.tb-plain) {
    font-size: 11px;
  }
  .tb-plain {
    font-size: 13px;
  }
}
</style>
